import React, { useCallback, useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInbox, faCheck } from "@fortawesome/free-solid-svg-icons";
import * as notify from "shared/notify";

import BackToButton from "components/task-nav/backToButton";
import Loading from "components/callback/loading";
import NotificationBar from "components/notification-bar";
import TaskNav from "components/task-nav/task-nav";
import SchedulingData from "./scheduling-data";
import Products from "contexts/attributes/products";
import { SourcePage } from "shared/taskTypeMap";

import { getShipment, completeShipment } from "./lib/schedulingData";
import { getTaskContextByKey, getTaskStatusTypes } from "components/task-list/lib/taskData";
import { getProfilingProductTaskInfo } from "components/attributes/lib/attribute-data";
import { useProductsValidity } from "shared/hooks";
import { useAlert } from "hooks/useAlert";
import { SettingsContext } from "contexts/settings";
import { useLastOpenedShipment } from "shared/hooks/api";
import { useAttributeGroups } from "shared/hooks/api/use-attribute-groups";
import { useViewOnlyPermissions } from "shared/hooks/use-view-only-permissions";

import "./scheduling.scss";
import { useNavigate, useParams } from "react-router-dom";

const SchedulingIndex = () => {
    const params = useParams();
    const navigate = useNavigate();
    const postLastVisitedShipment = useLastOpenedShipment();
    const newProfilingCodingWipKey = localStorage.getItem("newProfilingCodingWipKey");
    const { toggleAlert } = useAlert();
    const { settings } = useContext(SettingsContext);
    const isAttributesValid = useProductsValidity();
    const [invalidGroupAttributes, setInvalidGroupAttributes] = useState([]);
    const [state, setState] = useState({
        attributeList: [],
        codingWips: [],
        companyName: "",
        isLoaded: false,
        isOnHold: false,
        isCompleted: false,
        upcList: [],
        status: "",
        isBrandVerified: false,
    });
    const [showValidation, setShowValidaiton] = useState(false);
    const shipmentKey = params.shipmentKey;
    const [taskInfo, setTaskInfo] = useState([]);
    const [refreshPage, setRefreshPage] = useState(false);
    const { data: coreSchedulingDefinitions, refetch: refetchDefinitions } = useAttributeGroups(["IntakeProfilingNew"]);
    const taskType = "scheduling.default";

    const isVOCompleteBulkTask = useViewOnlyPermissions("CompleteTask", `bulk${SourcePage[taskType]}`);
    const isVOCompleteSingleTask = useViewOnlyPermissions("CompleteTask", `single${SourcePage[taskType]}`);
    const isVOAddAttribute = useViewOnlyPermissions("AddAttribute", SourcePage[taskType]);
    const isVOEditAttribute = useViewOnlyPermissions("EditAttribute", SourcePage[taskType]);
    const isVOCreateNote = useViewOnlyPermissions("CreateNote", SourcePage[taskType]);

    const handleInvalidGroupAttributes = useCallback((isValid) => {
        setInvalidGroupAttributes(isValid);
    }, []);

    const refresh = (isRefresh = false) => {
        setRefreshPage(isRefresh);
    };

    useEffect(() => {
        buildUpcData();
        postLastVisitedShipment(shipmentKey);
        refresh();
    }, [refreshPage]);

    useEffect(() => {
        if (settings.globalNotifications.messages.length) {
            toggleAlert({ group: "Profiling" });
        }
    }, [settings.globalNotifications.messages.length]);

    const taskTypeInfo = async (keys) => {
        if (keys) {
            const response = await getProfilingProductTaskInfo(keys);
            setTaskInfo(response);
        }
    };

    const buildUpcData = async () => {
        // List of UPCs in shipment
        const taskStatus = await getTaskStatusTypes("scheduling.default");
        const upcList = await getTaskContextByKey(shipmentKey, taskType);
        const shipmentData = await getShipment(shipmentKey);
        const sortedUpcList = _.sortBy(upcList, ["upc"]);
        const status =
            taskStatus && taskStatus.applicableStates
                ? taskStatus.applicableStates.filter((value) => value.stateId === shipmentData.shipmentProfilingStateId)[0]
                : "";

        const codingWips = sortedUpcList.map((u) => u.codingWipKey);
        const isTaskCompleted = status && status.state === "completed";
        const isBrandVerified = upcList[0].workflowTypeId === 2;

        setState({
            codingWips,
            companyName: shipmentData && shipmentData.locationInfo ? shipmentData.locationInfo.companyName : "",
            isLoaded: true,
            isOnHold: shipmentData.isOnHold,
            isCompleted: isTaskCompleted,
            upcList: sortedUpcList,
            status: status,
            isBrandVerified,
        });

        if (upcList) {
            const productKeys = upcList.map((val) => val.key);

            if (productKeys.length > 0) {
                taskTypeInfo(productKeys);
            }
        }
    };

    const completeScheduling = async () => {
        if (!state.isLoaded) {
            notify.error("Page has not loaded yet.");

            return;
        }

        if (!isAttributesValid) {
            setShowValidaiton(true);
            notify.error(invalidGroupAttributes.length > 0 ? "Required attributes are missing" : "Please fix validation issues in each UPC");

            return;
        }

        const response = await completeShipment(shipmentKey, state.codingWips, false, SourcePage[taskType]);

        if (!response) {
            return;
        }

        //BE sending array of Warning messages (list of objects) in response, if there is any archived brand/manufacturer used.
        if (response.errors && response.errors.length > 0) {
            let notifyMessages = _.map(response.errors, "message");

            notify.error(`${notifyMessages.join("<br/> <br/>")}`);

            return false;
        }

        backToList();
    };

    const saveAttributeErrors = (attrErrors) => {
        setAttributeErrors(attrErrors);
    };
    const backToList = () => {
        navigate("/profiling");
    };

    if (!state.isLoaded) {
        return <Loading />;
    }

    if (state.codingWips.length <= 0) {
        return (
            <div className="flex-column align-items-center">
                <h1 className="txt-center">Shipment has no products.</h1>
                {newProfilingCodingWipKey && (
                    <a
                        href={`/profiling/${newProfilingCodingWipKey}`}
                        className="button -text"
                        rel="noopener noreferrer"
                        onClick={() => localStorage.removeItem("newProfilingCodingWipKey")}
                    >
                        New Digital Lite Shipment
                    </a>
                )}
            </div>
        );
    }

    return (
        <div className="container">
            <Products
                products={state.upcList}
                codingWipKeys={state.codingWips}
                shipmentKey={shipmentKey}
            >
                <TaskNav>
                    <div className="button-group">
                        <BackToButton
                            text="Back to List"
                            onClick={backToList}
                        />
                        <h1 className="txt-center">Profiling</h1>
                        {state.status && (
                            <label className="-right text-status">
                                <b>Status: </b> {state.status.description}
                            </label>
                        )}
                        <a
                            className="button -right"
                            href={`/shipment-editor/${shipmentKey}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Edit Shipment"
                            onClick={() => localStorage.removeItem("newProfilingCodingWipKey")}
                        >
                            <FontAwesomeIcon icon={faInbox} />
                        </a>
                        <button
                            className="button -primary -right"
                            onClick={completeScheduling}
                            title="Complete Profiling"
                            disabled={isVOCompleteBulkTask || state.isCompleted || !isAttributesValid}
                        >
                            <FontAwesomeIcon icon={faCheck} />
                        </button>
                    </div>
                </TaskNav>

                {state.isBrandVerified && (
                    <NotificationBar
                        className="m-l-32"
                        variant="small"
                        type="info"
                    >
                        <span className="txt-bold">Brand Verified</span>
                    </NotificationBar>
                )}
                {state.isOnHold && (
                    <NotificationBar
                        type="info"
                        message="This is an on-hold product. Please select the necessary categories."
                    />
                )}

                <SchedulingData
                    coreSchedulingDefinitions={coreSchedulingDefinitions}
                    refetchDefinitions={refetchDefinitions}
                    companyName={state.companyName}
                    upcList={state.upcList}
                    setAttributeErrors={saveAttributeErrors}
                    showValidation={showValidation}
                    isCompleted={state.isCompleted}
                    tasktype={"scheduling.default"}
                    taskInfo={taskInfo}
                    refresh={refresh}
                    onValidateGroupAttributes={handleInvalidGroupAttributes}
                    invalidGroupAttributes={invalidGroupAttributes}
                    readOnlyAdd={isVOAddAttribute}
                    readOnlyEdit={isVOEditAttribute}
                    readOnlyComplete={isVOCompleteSingleTask}
                    readOnlyNotes={isVOCreateNote}
                />
            </Products>
        </div>
    );
};

export default SchedulingIndex;
